import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PrintIcon from "@mui/icons-material/Print";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { toast } from "react-toastify";
import CertificateCanvas from "../certificatePrint/CertificateCanvas";
import CertificateToolbar from "../certificatePrint/CertificateToolbar";
import {
  CertificatePageSettings,
  getCssAtPageSize,
  getPageSize,
} from "../certificatePrint/certificateTypes";
import { cloneCertificateTemplate } from "../certificatePrint/certificateTemplates";
import { useLoggedInClinic } from "../certificatePrint/useLoggedInClinic";
import { resolveCertificateLogo } from "../certificatePrint/getLoggedInClinic";
import "../certificatePrint/certificatePrint.css";
import PatientReportSettingsPanel from "./PatientReportSettingsPanel";
import {
  mergePatientReportPreviewData,
  PATIENT_REPORT_SAMPLE_DATA,
  PATIENT_REPORT_SAMPLE_CHARTS,
  PATIENT_REPORT_SAMPLE_CHECKLISTS,
  PATIENT_REPORT_SAMPLE_TABLES,
} from "./patientReportSampleData";
import {
  addPatientReportPage,
  createPatientReportTemplateId,
  findPatientReportTemplate,
  getDefaultPatientReportBuiltin,
  hydratePatientReportStore,
  loadPatientReportStore,
  printPatientReportSheet,
  removePatientReportPage,
  reportPageCount,
  savePatientReportStore,
  downloadPatientReportPdf,
} from "./patientReportStorage";
import baseUrl from "../../../../api/baseUrl";

function waitForPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function PatientReportDesigner() {
  const clinic = useLoggedInClinic();
  const stageRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);

  const [store, setStore] = useState(loadPatientReportStore);
  const storeRef = useRef(store);
  storeRef.current = store;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(0.75);
  const [mobilePane, setMobilePane] = useState<"design" | "preview">("preview");
  const [printing, setPrinting] = useState(false);

  const active = findPatientReportTemplate(store.templates, store.activeId);

  useEffect(() => {
    savePatientReportStore(store);
  }, [store]);

  useEffect(() => {
    baseUrl
      .get("/clinic/getBillData")
      .then((response) => {
        const saved = response?.data?.data?.patientReportPrint;
        if (!saved) return;
        setStore(hydratePatientReportStore(saved));
      })
      .catch(() => {
        /* keep local store */
      });
  }, []);

  const previewPage = useMemo(
    () => ({
      ...active.page,
      logoSrc: resolveCertificateLogo(clinic.printImages, active.page.logoSrc),
      letterheadSrc: active.page.letterheadSrc || "",
    }),
    [active.page, clinic.printImages]
  );

  const previewData = useMemo(
    () => mergePatientReportPreviewData(PATIENT_REPORT_SAMPLE_DATA, clinic),
    [clinic]
  );

  const updateActive = useCallback(
    (updater: (template: typeof active) => typeof active) => {
      setStore((current) => ({
        ...current,
        templates: current.templates.map((template) =>
          template.id === current.activeId ? updater(template) : template
        ),
      }));
    },
    []
  );

  const fitToScreen = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const size = getPageSize(active.page.orientation, active.page.size);
    const available = stage.clientWidth - 48;
    const pagePx = (size.widthMm * 96) / 25.4;
    setZoom(Math.max(0.35, Math.min(1.2, available / pagePx)));
  }, [active.page.orientation, active.page.size]);

  const selected = active.elements.find((item) => item.id === selectedId) ?? null;

  const pageCount = reportPageCount(active);

  const handleAddPage = () => {
    if (pageCount >= 6) {
      toast.info("Maximum of 6 pages.");
      return;
    }
    updateActive((template) => addPatientReportPage(template));
    toast.success("Page added. Extra sections were moved onto it.");
  };

  const handleRemovePage = () => {
    if (pageCount <= 1) return;
    updateActive((template) => removePatientReportPage(template));
    setSelectedId(null);
    toast.info("Removed the last report page.");
  };

  const persistToClinic = async (nextStore = storeRef.current) => {
    await baseUrl.patch("/clinic/savePatientReportPrint", {
      patientReportPrint: nextStore,
    });
  };

  const handlePrint = async () => {
    setSelectedId(null);
    setPrinting(true);
    await waitForPaint();
    await printPatientReportSheet(paperRef.current);
    setPrinting(false);
  };

  const handleDownloadPdf = async () => {
    const paper = paperRef.current;
    if (!paper) return;
    const previousZoom = zoom;
    setSelectedId(null);
    setPrinting(true);
    setZoom(1);
    await waitForPaint();
    try {
      await downloadPatientReportPdf({
        stack: paper,
        size: active.page.size,
        orientation: active.page.orientation,
        filename: `${active.name.replace(/\s+/g, "-").toLowerCase()}.pdf`,
      });
    } catch {
      toast.error("Could not prepare the PDF preview.");
    } finally {
      setZoom(previousZoom);
      setPrinting(false);
    }
  };

  return (
    <div className="certificate-designer">
      <style>
        {`@media print { @page { size: ${getCssAtPageSize(active.page.orientation, active.page.size)}; margin: 0; } }`}
      </style>
      <div className="certificate-mobile-tabs" data-no-print="true">
        <button
          type="button"
          className={mobilePane === "design" ? "is-active" : ""}
          onClick={() => setMobilePane("design")}
        >
          Design
        </button>
        <button
          type="button"
          className={mobilePane === "preview" ? "is-active" : ""}
          onClick={() => setMobilePane("preview")}
        >
          Preview
        </button>
      </div>

      <div className={`certificate-designer-grid is-${mobilePane}`}>
        <aside className="certificate-panel certificate-controls" data-no-print="true">
          <div className="certificate-panel-header">
            <span className="print-panel-eyebrow">Design controls</span>
            <h2>Report designer</h2>
            <p>
              Customise the live page preview. Placeholders stay bound to sample data until a
              patient report is generated.
            </p>
          </div>
          <PatientReportSettingsPanel
            templates={store.templates}
            activeId={store.activeId}
            defaultId={store.defaultId}
            page={active.page}
            elements={active.elements}
            selected={selected}
            onSelectTemplate={(id) => {
              setStore((current) => ({ ...current, activeId: id }));
              setSelectedId(null);
            }}
            onRename={(name) => updateActive((template) => ({ ...template, name }))}
            onSaveAsNew={() => {
              const copy = cloneCertificateTemplate(active, {
                id: createPatientReportTemplateId(),
                name: `${active.name} copy`,
              });
              setStore((current) => ({
                ...current,
                templates: [...current.templates, copy],
                activeId: copy.id,
              }));
              toast.success("Saved as a new template on this device.");
            }}
            onDuplicate={() => {
              const copy = cloneCertificateTemplate(active, {
                id: createPatientReportTemplateId(),
                name: `${active.name} copy`,
              });
              setStore((current) => ({
                ...current,
                templates: [...current.templates, copy],
                activeId: copy.id,
              }));
              toast.success("Template duplicated.");
            }}
            onReset={() => {
              const builtin = getDefaultPatientReportBuiltin(active.id);
              if (!builtin) return;
              updateActive(() => ({
                ...builtin,
                id: active.id,
                name: active.name,
              }));
              toast.info("Reset to the built-in layout for this template.");
            }}
            onSetDefault={() => {
              setStore((current) => ({ ...current, defaultId: current.activeId }));
              toast.success("Set as the default patient report template.");
            }}
            onPageChange={(patch: Partial<CertificatePageSettings>) =>
              updateActive((template) => ({ ...template, page: { ...template.page, ...patch } }))
            }
            onElementChange={(id, patch) =>
              updateActive((template) => ({
                ...template,
                elements: template.elements.map((item) =>
                  item.id === id ? { ...item, ...patch } : item
                ),
              }))
            }
            onToggleField={(ids, visible) =>
              updateActive((template) => ({
                ...template,
                elements: template.elements.map((item) =>
                  ids.includes(item.id) ? { ...item, visible } : item
                ),
              }))
            }
            onSelectElement={setSelectedId}
            onSave={() => {
              const nextStore = {
                ...storeRef.current,
                defaultId: storeRef.current.activeId,
              };
              setStore(nextStore);
              persistToClinic(nextStore)
                .then(() =>
                  toast.success("Template saved and set as the default for Generate Report.")
                )
                .catch(() => toast.error("Could not save the report template."));
            }}
            pageCount={pageCount}
            onAddPage={handleAddPage}
            onRemovePage={handleRemovePage}
          />
        </aside>

        <section className="certificate-panel certificate-preview">
          <div className="certificate-panel-header" data-no-print="true">
            <span className="print-panel-eyebrow">Patient report preview</span>
            <h2>
              {active.page.size} patient report · {pageCount} page{pageCount === 1 ? "" : "s"}
            </h2>
            <p>Zoom is editor-only. Print uses the real page without controls or guides.</p>
          </div>
          <div className="certificate-preview-body">
            <div data-no-print="true">
              <CertificateToolbar zoom={zoom} onZoomChange={setZoom} onFit={fitToScreen} />
            </div>
            <div className="certificate-stage" ref={stageRef}>
              <CertificateCanvas
                page={previewPage}
                elements={active.elements}
                selectedId={printing ? null : selectedId}
                previewData={previewData}
                previewTables={PATIENT_REPORT_SAMPLE_TABLES}
                previewCharts={PATIENT_REPORT_SAMPLE_CHARTS}
                previewChecklists={PATIENT_REPORT_SAMPLE_CHECKLISTS}
                interactive={!printing}
                zoom={printing ? 1 : zoom}
                pageCount={pageCount}
                paperRef={paperRef}
                onSelect={setSelectedId}
                onElementsChange={(updater) =>
                  updateActive((template) => ({
                    ...template,
                    elements: updater(template.elements),
                  }))
                }
              />
            </div>
            <div className="certificate-footer-bar mt-4" data-no-print="true">
              <button
                type="button"
                className="certificate-btn certificate-btn-primary"
                onClick={handleAddPage}
                disabled={pageCount >= 6}
              >
                <AddIcon sx={{ fontSize: 16 }} /> Add page
              </button>
              <button
                type="button"
                className="certificate-btn"
                onClick={handleRemovePage}
                disabled={pageCount <= 1}
              >
                <RemoveIcon sx={{ fontSize: 16 }} /> Remove page
              </button>
              <button type="button" className="certificate-btn" onClick={handleDownloadPdf}>
                <PictureAsPdfIcon sx={{ fontSize: 16 }} /> Download PDF
              </button>
              <button type="button" className="certificate-btn" onClick={handlePrint}>
                <PrintIcon sx={{ fontSize: 16 }} /> Print
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default PatientReportDesigner;
